let guarded = false;

/**
 * Keep React from crashing when something outside it has moved its DOM nodes.
 *
 * Chrome's built-in page translation swaps text nodes for <font> wrappers. When
 * React later removes or inserts next to one of the originals, the browser
 * throws "Failed to execute 'removeChild' on 'Node'" (or 'insertBefore'), and
 * the route error boundary replaces the page.
 *
 * Our code never moves nodes React renders (third-party widgets only write
 * into empty containers), so a mismatch here always comes from outside.
 * Workaround from https://github.com/facebook/react/issues/11538. Client only.
 */
export function guardDomAgainstTranslators(): void {
  if (typeof Node !== "function" || guarded) return;
  guarded = true;

  const removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T) {
    if (child.parentNode !== this) {
      console.warn("Skipped removing a node moved by another script", child);
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  const insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ) {
    if (child && child.parentNode !== this) {
      console.warn(
        "Skipped inserting next to a node moved by another script",
        child,
      );
      return node;
    }
    return insertBefore.call(this, node, child) as T;
  };
}
